import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { getMe, login as apiLogin, logout as apiLogout } from '../api/client.js';

const AuthContext = createContext(null);

// Roles with full access to the XM (customer-experience) dashboard.
const XM_ADMIN_ROLES = ['xm_admin', 'super_admin'];
// Roles with access to the marketing (sentiment) dashboard.
const MARKETING_ROLES = ['marketing_admin', 'super_admin'];

/** Which dashboard a role lands on. Mirrors the backend's dashboard_for_role
 *  so routing works even if the API response omits the field. */
export function dashboardForRole(role) {
  if (role === 'super_admin') return 'both';
  if (role === 'marketing_admin') return 'marketing';
  return 'xm';
}

/**
 * Auth is cookie-based — no token ever touches JavaScript. On mount we
 * probe /auth/me: if the browser has a valid cookie, we get a User back;
 * otherwise the fetch throws (401) and we stay unauthenticated.
 */
export function AuthProvider({ children }) {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [bootstrapping, setBootstrapping] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      try {
        const me = await getMe();
        if (!cancelled) setUser(me);
      } catch {
        // No session yet — that's fine, LoginPage will handle it.
        if (!cancelled) setUser(null);
      } finally {
        if (!cancelled) setBootstrapping(false);
      }
    }

    bootstrap();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (email, password) => {
    // Server sets the httpOnly cookie in the response. We fetch /me
    // right after to populate context with role/email/full_name.
    await apiLogin(email, password);
    const me = await getMe();
    setUser(me);
    return me;
  }, []);

  const logout = useCallback(async () => {
    try {
      await apiLogout();
    } catch {
      // Even if the server call fails, clear local state.
    }
    setUser(null);
    navigate('/login', { replace: true });
  }, [navigate]);

  const value = useMemo(
    () => ({
      user,
      bootstrapping,
      isAuthenticated: !!user,
      role: user?.role || null,
      // Prefer the server-provided field; fall back to deriving from role so
      // a page refresh (which hydrates from /auth/me) still routes correctly.
      activeDashboard: user
        ? user.dashboard || dashboardForRole(user.role)
        : null,
      isAdmin: () => XM_ADMIN_ROLES.includes(user?.role),
      isMarketing: () => MARKETING_ROLES.includes(user?.role),
      isSuperAdmin: () => user?.role === 'super_admin',
      login,
      logout,
    }),
    [user, bootstrapping, login, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return ctx;
}
